import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { Button, NotificationBox } from 'src/components';
import { IS_DEVELOPMENT } from 'src/config';
import { sanitizeRedirectUrl } from 'src/utils/sanitizeRedirectUrl';

const searchSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
  redirect_path: z.string().optional(),
});

export const Route = createFileRoute('/_public/authentication-error')({
  validateSearch: zodValidator(searchSchema),
  component: () => {
    const search = Route.useSearch();
    const { t } = useLingui();

    const redirectPath = sanitizeRedirectUrl(search.redirect_path);

    return (
      <div data-cy="error-page" className="w-full bg-neutral-light-2">
        <div className="m-auto flex max-w-screen-md flex-col items-center gap-24 px-24 py-64 md:px-64 lg:py-128">
          <Typography variant="heading-lg" className="text-center">
            <Trans>Something went wrong during authentication</Trans>
          </Typography>

          {search.error_description && IS_DEVELOPMENT && (
            <NotificationBox title={search.error ?? t`Error`} variant="error">
              {search.error_description}
            </NotificationBox>
          )}

          <Typography variant="body-md" className="text-center">
            <Trans>Please try again later or login with another account.</Trans>
          </Typography>

          <div className="mt-32 flex gap-24">
            {redirectPath ? (
              <Button as="a" to={redirectPath}>
                <Trans>Go back</Trans>
              </Button>
            ) : (
              <Button as="a" to="/">
                <Trans>Go home</Trans>
              </Button>
            )}

            <Button variant="secondary" as="a" to="/logout">
              <Trans>Login with another account</Trans>
            </Button>
          </div>
        </div>
      </div>
    );
  },
});
